import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, X } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ShareReportDialog({ candId, candName, onClose, onSent }) {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [members, setMembers] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/tenant/members").then(r => setMembers(r.data.members || [])).catch(() => {});
  }, []);

  const addEmail = (raw) => {
    const parts = raw.split(/[\s,;]+/).map(x => x.trim().toLowerCase()).filter(Boolean);
    const bad = parts.filter(p => !EMAIL_RE.test(p));
    if (bad.length) toast.error(`Not a valid email: ${bad.join(", ")}`);
    const good = parts.filter(p => EMAIL_RE.test(p));
    if (good.length) setEmails(prev => [...new Set([...prev, ...good])].slice(0, 10));
    setInput(bad.join(" "));
  };

  const onKey = (e) => {
    if (["Enter", ",", ";", "Tab"].includes(e.key) && input.trim()) { e.preventDefault(); addEmail(input); }
    else if (e.key === "Backspace" && !input && emails.length) setEmails(emails.slice(0, -1));
  };

  const q = input.trim().toLowerCase();
  const suggestions = q ? members.filter(m => !emails.includes((m.email || "").toLowerCase()) &&
    ((m.email || "").toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q))).slice(0, 5) : [];

  const send = async (e) => {
    e.preventDefault();
    const pending = input.trim() ? input.split(/[\s,;]+/).map(x => x.trim().toLowerCase()).filter(x => EMAIL_RE.test(x)) : [];
    const all = [...new Set([...emails, ...pending])];
    if (!all.length) { toast.error("Add at least one recipient email"); return; }
    setSending(true);
    try {
      await api.post(`/candidates/${candId}/share-report`, { emails: all, message });
      toast.success(`Report emailed to ${all.length} recipient${all.length > 1 ? "s" : ""}`);
      onSent?.();
      onClose();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Could not send email"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={send} className="tp-card p-6 w-full max-w-lg space-y-4" data-testid="share-report-modal">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg font-semibold">Email report</div>
          <button type="button" data-testid="share-report-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          Send {candName || "this candidate"}'s PDF report and recording links to hiring managers. You'll be CC'd, and replies come straight to you.
        </div>
        <div className="relative">
          <label className="text-xs text-slate-400 uppercase tracking-wider">To</label>
          <div className="mt-1 flex flex-wrap gap-1.5 px-2 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus-within:border-indigo-500">
            {emails.map(em => (
              <span key={em} data-testid={`share-recipient-${em}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-indigo-500/15 text-indigo-200">
                {em}
                <button type="button" onClick={() => setEmails(emails.filter(x => x !== em))} className="hover:text-white"><X className="w-3 h-3"/></button>
              </span>
            ))}
            <input
              data-testid="share-email-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              onBlur={() => input.trim() && setTimeout(() => addEmail(input), 150)}
              placeholder={emails.length ? "" : "manager@company.com"}
              className="flex-1 min-w-[160px] bg-transparent outline-none text-sm px-1"
            />
          </div>
          {suggestions.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 z-10 tp-card-elev py-1 shadow-lg" data-testid="share-suggestions">
              {suggestions.map(m => (
                <button type="button" key={m.id} data-testid={`share-suggestion-${m.email}`}
                  onMouseDown={(e) => { e.preventDefault(); setEmails(prev => [...new Set([...prev, m.email.toLowerCase()])]); setInput(""); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5">
                  <span className="text-slate-200">{m.name || m.email}</span>
                  <span className="text-slate-500 text-xs ml-2">{m.email}</span>
                </button>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-1">Press Enter or comma to add · up to 10 recipients</div>
        </div>
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider">Note (optional)</label>
          <textarea
            data-testid="share-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Hi team — strong candidate for the role, would love your take before Friday."
            className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={sending} data-testid="share-report-send" className="text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 inline-flex items-center gap-1.5">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            {sending ? "Sending…" : "Send report"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ReportShareHistory({ shares }) {
  if (!shares?.length) return null;
  return (
    <div className="mt-6 tp-card p-6" data-testid="report-share-history">
      <div className="font-display text-lg font-semibold mb-3">Report shared with</div>
      <div className="divide-y divide-[color:var(--bd-sub)]">
        {shares.map(s => (
          <div key={s.id} data-testid={`report-share-${s.id}`} className="py-3 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 text-sm">
            <div className="sm:w-44 shrink-0 text-xs text-slate-500">{new Date(s.sent_at).toLocaleString()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-200 break-words">{s.recipients.join(", ")}</div>
              <div className="text-xs text-slate-500 mt-0.5">by {s.sent_by_name}{s.message ? ` · "${s.message.length > 120 ? s.message.slice(0, 120) + "…" : s.message}"` : ""}</div>
            </div>
            <span className={s.status === "sent" ? "tp-chip tp-chip-emerald self-start" : "tp-chip tp-chip-rose self-start"} title={s.error || ""}>{s.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
